import { css } from '@emotion/react';
import { Box } from '@inflearn/ds-react';
import type { Language } from '@inflearn/i18n-tools';
import { NativeSelect } from '@mantine/core';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import type React from 'react';
import { useState } from 'react';

import {
  GetUserProfileCourseSort,
  fetchClientApiV1CourseUserUserId,
} from '../../../../resources/@types/courseSchema';
import { logger } from '../../../../resources/Logger';
import CourseCardSkeleton from '../../../components/common/CourseCardSkeleton';
import EmptyStates from '../../../components/common/EmptyStates';
import CustomHead from '../../../components/layout/CustomHead';
import Layout from '../../../components/layout/Layout';
import CourseItem from '../../../components/pages/profile/CourseItem/CourseItem';
import GridList from '../../../components/pages/profile/GridList/GridList';
import ImpressionArea from '../../../components/pages/profile/ImpressionArea/ImpressionArea';
import ReFetchInfinityScroll from '../../../components/pages/profile/ReFetchInfinityScroll/ReFetchInfinityScroll';
import SectionHeader from '../../../components/pages/profile/SectionHeader/SectionHeader';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { COURSE_SORT } from '../../../constants/course';
import { USERS_URL } from '../../../constants/urls';
import { useCoursesPageReactionMutate } from '../../../hooks/useCoursesPageReactionMutate';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import { useLoginModalContext } from '../../../providers/ControlLoginModalContextProvider';
import {
  getInstructorIdCourseInfinityQueryKey,
  getUseCourseUserUserIdQueryQueryUrl,
  useInstructorIdCourseInfinityQuery,
} from '../../../queries/useCourseUserUserIdQuery';
import { normalizeCourseSort } from '../../../utils/course/courseType';
import {
  type WithCommonNextPage,
  withProfileUserProps,
} from '../../../utils/page/withProfileUserProps';
import { withRemovedPageFallback } from '../../../utils/page/withRemovedPageFallback';

const COURSE_ITEM_SORT = GetUserProfileCourseSort.RECENT;
const COURSE_ITEM_PAGE_LIMIT = 12;

const UserCoursesPage: WithCommonNextPage = () => {
  const { t } = useTranslation();
  const [sort, setSort] = useState<GetUserProfileCourseSort>(COURSE_ITEM_SORT);
  const { open: openLoginModal } = useLoginModalContext();

  const { query } = useRouter();
  const { data: profile } = useUserProfileQuery({ id: Number(query.id) });

  const {
    data,
    refetch,
    fetchNextPage,
    isError,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useInstructorIdCourseInfinityQuery({
    userId: String(profile?.id),
    queryObject: {
      pageSize: COURSE_ITEM_PAGE_LIMIT,
      sort,
    },
    options: {
      enabled: Boolean(profile?.id),
    },
  });
  const courseTotalCount = data[0]?.totalCount;

  const onChangeSort = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    setSort(normalizeCourseSort(value));
  };

  const userName = profile?.name;

  const courseSortOptions = Object.values(COURSE_SORT).map((option) => ({
    ...option,
    label: t(option.label),
  }));

  return (
    <Layout isInstructor={Boolean(profile?.isInstructor)} isInactive={Boolean(profile?.isInactive)}>
      <CustomHead
        title={t('{{userName}}님의 강의', { userName: userName })}
        description={t(
          '인프런 {{userName}}님의 강의 페이지 입니다. - {{userName}}님 소개 | 인프런',
          { userName: userName },
        )}
        image={profile?.thumbnailUrl}
      />

      <SectionHeader styles={css({ marginBottom: '30px !important' })}>
        <SectionHeader.Title title={t('강의')}>
          {!isNil(courseTotalCount) && courseTotalCount > 0 ? (
            <SectionHeader.TotalCount totalCount={courseTotalCount} />
          ) : null}
        </SectionHeader.Title>
        <NativeSelect
          defaultValue={COURSE_SORT.RECENT.value}
          value={sort}
          onChange={onChangeSort}
          data={courseSortOptions}
        />
      </SectionHeader>
      <EmptyStates height={300} onRetry={refetch} isError={isError}>
        <GridList gap="48px 16px">
          {isLoading ? (
            <DeferredComponent>
              <CourseCardSkeleton count={6} />
            </DeferredComponent>
          ) : (
            data.map((page) =>
              page?.items?.map((course) => (
                <Box key={course.id} component="li" css={{ flex: '1 0 0' }}>
                  {profile && (
                    <CourseItem
                      course={course}
                      profile={profile}
                      useReactionMutate={useCoursesPageReactionMutate}
                      isLoading={isFetching}
                      onOpenLoginModal={openLoginModal}
                    />
                  )}
                </Box>
              )),
            )
          )}
          {isFetching || isFetchingNextPage ? <CourseCardSkeleton count={6} /> : null}
          <ImpressionArea
            isImpressed={Boolean(hasNextPage) && !isFetchingNextPage}
            impressionRatio={0.5}
            onImpressionStart={fetchNextPage}
          />
        </GridList>
        {hasNextPage && isError ? <ReFetchInfinityScroll fetchNext={fetchNextPage} /> : null}
      </EmptyStates>
    </Layout>
  );
};

export const getServerSideProps = withProfileUserProps(
  async ({ context, queryClient, isInstructor }) => {
    const userId = context.params?.id ? String(context.params?.id) : '';
    const lang = context.locale as Language;

    if (!isInstructor) {
      return {
        redirect: {
          destination: USERS_URL.HOME({ userId: userId, lang }),
          permanent: false,
        },
      };
    }

    try {
      await queryClient.fetchInfiniteQuery(
        getInstructorIdCourseInfinityQueryKey({
          userId,
          pageSize: COURSE_ITEM_PAGE_LIMIT,
          sort: COURSE_ITEM_SORT,
        }),
        () =>
          fetchClientApiV1CourseUserUserId(
            getUseCourseUserUserIdQueryQueryUrl({
              userId,
              pageNumber: 1,
              pageSize: COURSE_ITEM_PAGE_LIMIT,
              sort: COURSE_ITEM_SORT,
              lang,
            }),
          ),
      );

      return {
        props: {},
      };
    } catch (error) {
      logger.info(`[ERROR in SSR courses:] ${error}`);

      return {
        props: {
          isApiError: true,
        },
      };
    }
  },
);

export default withRemovedPageFallback(UserCoursesPage);
